import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { Icon } from "../Icon";

// Label above the control, hint and error below it, all wired with
// aria-describedby / aria-invalid (design.md "Inputs": label above, no
// floating labels, error text below in semantic red).

interface FieldShellProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  optional?: boolean;
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}

export function FieldShell({ label, hint, error, optional, children }: FieldShellProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {label}
        {optional ? <span className="field-optional"> (optional)</span> : null}
      </label>
      {children({ id, describedBy, invalid: !!error })}
      {hint ? (
        <div className="field-hint" id={hintId}>
          {hint}
        </div>
      ) : null}
      {error ? (
        <div className="field-error" id={errorId} role="alert">
          <Icon name="error" className="field-error-icon" />
          <span>{error}</span>
        </div>
      ) : null}
    </div>
  );
}

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  optional?: boolean;
  mono?: boolean;
}

export function Input({ label, hint, error, optional, mono, className, ...rest }: InputProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} optional={optional}>
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          className={["input", mono ? "input-mono" : "", className ?? ""].filter(Boolean).join(" ")}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          {...rest}
        />
      )}
    </FieldShell>
  );
}

interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id"> {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  optional?: boolean;
}

export function Textarea({ label, hint, error, optional, className, ...rest }: TextareaProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} optional={optional}>
      {({ id, describedBy, invalid }) => (
        <textarea
          id={id}
          className={["textarea", className ?? ""].filter(Boolean).join(" ")}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          {...rest}
        />
      )}
    </FieldShell>
  );
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "id"> {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  optional?: boolean;
  options: Array<{ value: string; label: string; disabled?: boolean }>;
  placeholder?: string;
}

export function Select({ label, hint, error, optional, options, placeholder, className, ...rest }: SelectProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} optional={optional}>
      {({ id, describedBy, invalid }) => (
        <select
          id={id}
          className={["select", className ?? ""].filter(Boolean).join(" ")}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          {...rest}
        >
          {placeholder !== undefined ? (
            <option value="" disabled>
              {placeholder}
            </option>
          ) : null}
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </FieldShell>
  );
}

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "id"> {
  label: ReactNode;
  hint?: ReactNode;
}

export function Checkbox({ label, hint, ...rest }: CheckboxProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  return (
    <div className="stack-sm">
      <div className="checkbox">
        <input type="checkbox" id={id} aria-describedby={hintId} {...rest} />
        <label htmlFor={id}>{label}</label>
      </div>
      {hint ? (
        <div className="field-hint" id={hintId}>
          {hint}
        </div>
      ) : null}
    </div>
  );
}

interface RadioGroupProps<V extends string> {
  legend: ReactNode;
  name: string;
  value: V | "";
  onChange: (value: V) => void;
  options: Array<{ value: V; label: ReactNode; hint?: ReactNode }>;
}

export function RadioGroup<V extends string>({ legend, name, value, onChange, options }: RadioGroupProps<V>) {
  const base = useId();
  return (
    <fieldset className="radio-group">
      <legend>{legend}</legend>
      {options.map((o) => {
        const id = `${base}-${o.value}`;
        return (
          <div key={o.value} className="radio">
            <input
              type="radio"
              id={id}
              name={name}
              value={o.value}
              checked={value === o.value}
              onChange={() => onChange(o.value)}
              aria-describedby={o.hint ? `${id}-hint` : undefined}
            />
            <label htmlFor={id}>
              {o.label}
              {o.hint ? (
                <span id={`${id}-hint`} className="field-hint" style={{ display: "block" }}>
                  {o.hint}
                </span>
              ) : null}
            </label>
          </div>
        );
      })}
    </fieldset>
  );
}
